import React, { useState, useEffect, useContext } from 'react';
import { Box, TextField, Button, Typography } from '@mui/material';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import QuoteGrid from './QuoteGrid';
import QuoteModal from './saveQuote';
import config from '../config';
import { AuthContext } from './AuthContext';

const FormComponent = () => {
  const { auth } = useContext(AuthContext);
  const location = useLocation();
  const initialQuoteNum = location.state?.quoteNum || '';

  const [quoteNum1, setQuoteNum1] = useState(initialQuoteNum);
  const [quoteNum2, setQuoteNum2] = useState('');
  const [quote1, setQuote1] = useState([]);
  const [quote2, setQuote2] = useState([]);
  const [match, setMatch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialQuoteNum) {
      setQuoteNum1(initialQuoteNum);
    }
  }, [initialQuoteNum]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setQuote1([]);
    setQuote2([]);
    setMatch(false);

    const [id1, v1] = quoteNum1.split('.');
    const [id2, v2] = quoteNum2.split('.');

    if (!id1 || !v1 || !id2 || !v2) {
      setError('Please enter both quote numbers in the correct format (e.g., 123.1)');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${config.BaseUrl}/api/quotes/compare/${id1}/${v1}/${id2}/${v2}/`,
        {
          headers: {
            Authorization: `Bearer ${auth}`,
          },
        }
      );
      setQuote1(response.data.quote1.items);
      setQuote2(response.data.quote2.items);
      setMatch(response.data.match);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Error fetching quotes. Please check the quote numbers and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setQuoteNum1('');
    setQuoteNum2('');
    setQuote1([]);
    setQuote2([]);
    setMatch(false);
    setError(null);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" component="h1" gutterBottom>
        Compare Quotes
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: 2,
          width: '100%',
          borderRadius: '10px',
          boxShadow: 4,
        }}
        noValidate
        autoComplete="off"
      >
        <Box sx={{ display: 'flex', gap: 2, width: '50%', mr: 1 }}>
          <TextField
            id="quoteNum1"
            label="First Quote Number"
            variant="outlined"
            size="small"
            value={quoteNum1}
            onChange={(e) => setQuoteNum1(e.target.value)}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, width: '50%' }}>
          <TextField
            id="quoteNum2"
            label="Second Quote Number"
            variant="outlined"
            size="small"
            value={quoteNum2}
            onChange={(e) => setQuoteNum2(e.target.value)}
            fullWidth
          />
          <Button variant="contained" color="primary" type="submit" disabled={loading}>
            {loading ? 'Loading...' : 'Submit'}
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleReset}>
            Reset
          </Button>
        </Box>
      </Box>

      {error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {quote1.length > 0 && (
        <Box sx={{ mt: 4, width: '100%', borderRadius: '10px', boxShadow: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ m: 2 }} color={match ? 'green' : 'error'}>
            {match ? 'Complete Match' : 'Incomplete Match'}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
            <Box sx={{ width: '50%', mr: 1 }}>
              <QuoteGrid data={quote1} />
              <QuoteModal quoteNum={quoteNum1} />
            </Box>
            <Box sx={{ width: '50%', ml: 1 }}>
              <QuoteGrid data={quote2} />
              <QuoteModal quoteNum={quoteNum2} />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FormComponent;